// 결과 본문 + 문장 끝 작은 근거 번호. 번호는 CSS ::after 로 그려 끌어서 복사해도 글에 섞이지 않는다.
import React, { useEffect, useMemo, useState } from 'react';
import { buildPieces, numberUnits, readShowNumbers, sentenceNumbers, writeShowNumbers } from './saenggibuEvidence';
import './SaenggibuEvidence.css';

export const useShowEvidenceNumbers = () => {
  const [show, setShow] = useState(readShowNumbers);
  useEffect(() => {
    const handle = (event) => setShow(Boolean(event?.detail?.value));
    window.addEventListener('saenggibuEvidenceNumbers', handle);
    return () => window.removeEventListener('saenggibuEvidenceNumbers', handle);
  }, []);
  const update = (value) => {
    setShow(value);
    writeShowNumbers(value);
  };
  return [show, update];
};

/** 문장 끝 번호. 누르면 그 문장으로 근거 보기를 연다. */
export default function SaenggibuEvidenceText({ content, evidence, showNumbers = true, onOpen }) {
  const pieces = useMemo(() => (evidence?.item ? buildPieces(content, evidence.item) : null), [content, evidence]);
  const numbers = useMemo(() => numberUnits(evidence?.item, evidence?.sources), [evidence]);

  if (!pieces || !showNumbers) return <>{content}</>;

  return (
    <>
      {pieces.map((piece, index) => {
        if (piece.type === 'gap') return <React.Fragment key={`g${index}`}>{piece.text}</React.Fragment>;
        const marks = sentenceNumbers(piece.units, numbers);
        const isSummary = marks.length === 0;
        return (
          <React.Fragment key={`s${piece.index}`}>
            {piece.text}
            <button
              type="button"
              className={`sgev-cite${isSummary ? ' sgev-cite--summary' : ''}`}
              aria-label={isSummary ? `${piece.index + 1}번째 문장: 정리한 문장, 근거 보기` : `${piece.index + 1}번째 문장 근거 ${marks.join(', ')}번 보기`}
              data-testid="sgev-cite"
              data-label={isSummary ? '정리' : marks.join(',')}
              onClick={() => onOpen?.(piece.index)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

/** 결과 아래 한 줄: "넣은 내용 5개 중 4개를 썼어요 · 근거 보기" */
export function SaenggibuEvidenceSummary({ status, evidence, onOpen, showNumbers, onToggleNumbers, compact = false }) {
  if (status === 'off' || status === 'failed') return null;
  if (status !== 'ready' || !evidence?.item) {
    return (
      <div className={`sgev-summary${compact ? ' sgev-summary--compact' : ''}`} data-testid="sgev-summary" aria-live="polite">
        <span className="sgev-summary__dot" aria-hidden="true" />
        <span className="sgev-summary__text sgev-summary__text--muted">어떤 내용으로 썼는지 찾고 있어요</span>
      </div>
    );
  }
  const { total = 0, used = 0 } = evidence.item.summary || {};
  return (
    <div className={`sgev-summary${compact ? ' sgev-summary--compact' : ''}`} data-testid="sgev-summary">
      <span className="sgev-summary__text">
        {total > 0 ? <>넣은 내용 <strong>{total}개</strong> 중 <strong>{used}개</strong>를 썼어요</> : '넣은 내용을 정리해 썼어요'}
      </span>
      <button type="button" className="sgev-summary__open" onClick={() => onOpen?.(null)} data-testid="sgev-open">
        근거 보기
      </button>
      {onToggleNumbers && (
        <button
          type="button"
          className="sgev-summary__toggle"
          aria-pressed={showNumbers}
          onClick={() => onToggleNumbers(!showNumbers)}
        >
          {showNumbers ? '번호 숨기기' : '번호 보기'}
        </button>
      )}
    </div>
  );
}
